/**
 * AiReviewSection — the LLM review of a run's failed evaluations, on the run detail page.
 *
 * The report task samples the failed evaluations (scored below threshold) and has an
 * LLM review each one against the conversation: is the evaluator right (Real Issue /
 * False Positive / Uncertain), root cause, suggestion, action, severity. Only the
 * critical ones become the run's Failure Cases (Home Error Log, Insights), so a run can
 * have failed evaluations and no Failure Case. This lists every reviewed one.
 * Data: GET /runs/{id}/evaluations?has_analysis=true (at most the sample, never cut).
 */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AskEvalAgent, { askValue } from "../../../components/AskEvalAgent/AskEvalAgent";
import { evalApi } from "../../../client/EvalApiClient";

export const VERDICTS = ["Real Issue", "False Positive", "Uncertain"] as const;
export const SEVERITIES = ["critical", "warning", "info"] as const;

const VERDICT_STYLE: Record<string, { bg: string; fg: string }> = {
  "Real Issue": { bg: "rgba(239,68,68,0.15)", fg: "#ef4444" },
  "False Positive": { bg: "rgba(34,197,94,0.15)", fg: "#22c55e" },
  Uncertain: { bg: "rgba(234,179,8,0.15)", fg: "#ca8a04" },
};
const SEVERITY_STYLE: Record<string, { bg: string; fg: string }> = {
  critical: { bg: "rgba(220,38,38,0.1)", fg: "#dc2626" },
  warning: { bg: "rgba(234,88,12,0.1)", fg: "#ea580c" },
  info: { bg: "rgba(107,114,128,0.1)", fg: "#6b7280" },
};

export interface ReviewedEvaluation {
  evaluator: string;
  session_id: string;
  trace_id?: string | null;
  level?: string;
  score?: number | null;
  threshold?: number | null;
  analysis: {
    verdict?: string;
    severity?: string;
    action?: string;
    root_cause?: string;
    suggestion?: string;
  };
}

function Pill({ text, style }: { text: string; style?: { bg: string; fg: string } }) {
  const s = style || SEVERITY_STYLE.info;
  return (
    <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", background: s.bg, color: s.fg }}>
      {text}
    </span>
  );
}

const num = (v: unknown) => (typeof v === "number" ? v.toFixed(2) : "–");

/** Counts per value, in the given order, then any other value seen. */
export function countBy(values: (string | undefined)[], order: readonly string[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v || "–", (counts.get(v || "–") || 0) + 1);
  const known = order.filter((k) => counts.has(k)).map((k) => [k, counts.get(k)!] as [string, number]);
  const rest = [...counts].filter(([k]) => !order.includes(k));
  return [...known, ...rest];
}

/** Sort rank of a severity: the known ones in order, then anything else (e.g. the old
 * pipeline's "high"), then a missing one. */
export function severityRank(severity?: string): number {
  if (!severity) return SEVERITIES.length + 1;
  const i = (SEVERITIES as readonly string[]).indexOf(severity);
  return i < 0 ? SEVERITIES.length : i;
}

export default function AiReviewSection({ requestId, agent }: { requestId: string; agent?: string }) {
  const { t } = useTranslation("eval");
  const [rows, setRows] = useState<ReviewedEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const latest = useRef(0); // the newest request wins (run / agent switches)

  useEffect(() => {
    const request = ++latest.current;
    setLoading(true);
    setFailed(false);
    evalApi
      .getRunEvaluations(requestId, agent, { hasAnalysis: true })
      .then((data: any) => {
        if (request !== latest.current) return;
        setRows((data?.evaluations || []).filter((ev: any) => ev && ev.analysis));
      })
      .catch(() => {
        if (request !== latest.current) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
  }, [requestId, agent]);

  const verdicts = countBy(rows.map((r) => r.analysis.verdict), VERDICTS);
  const severities = countBy(rows.map((r) => r.analysis.severity), SEVERITIES);
  const falsePositives = rows.filter((r) => r.analysis.verdict === "False Positive").length;
  const sorted = [...rows].sort(
    (a, b) => severityRank(a.analysis.severity) - severityRank(b.analysis.severity) || (a.score ?? 0) - (b.score ?? 0),
  );
  const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", fontSize: 12, color: "var(--eval-text-secondary)", borderBottom: "1px solid var(--eval-border, #e5e7eb)", whiteSpace: "nowrap" };
  const td: React.CSSProperties = { padding: "8px 10px", fontSize: 12, verticalAlign: "top", borderBottom: "1px solid var(--eval-border, #e5e7eb)" };

  return (
    <div className="eval-reports-continuous__card" data-testid="ai-review">
      <h3 className="eval-reports-continuous__section-title">
        {t("continuous.aiReview.title", { count: loading ? "..." : rows.length })}
        <AskEvalAgent question={t("askAgent.aiReview", { runId: requestId })} />
      </h3>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--eval-text-secondary)" }}>{t("continuous.aiReview.description")}</p>
      {loading ? (
        <div style={{ fontSize: 13, color: "var(--eval-text-secondary)" }}>{t("continuous.aiReview.loading")}</div>
      ) : failed ? (
        <div style={{ fontSize: 13, color: "#dc2626" }}>{t("continuous.aiReview.failed")}</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--eval-text-secondary)" }}>{t("continuous.aiReview.empty")}</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 12, fontSize: 12 }}>
            <strong>{t("continuous.aiReview.verdicts")}</strong>
            {verdicts.map(([v, n]) => (
              <Pill key={v} text={`${v} ${n}`} style={VERDICT_STYLE[v]} />
            ))}
            <span style={{ marginLeft: 12 }}>
              {t("continuous.aiReview.falsePositiveShare", { n: falsePositives, total: rows.length, pct: Math.round((falsePositives / rows.length) * 100) })}
            </span>
            <strong style={{ marginLeft: 12 }}>{t("continuous.aiReview.severities")}</strong>
            {severities.map(([s, n]) => (
              <Pill key={s} text={`${s} ${n}`} style={SEVERITY_STYLE[s]} />
            ))}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>{t("continuous.aiReview.colVerdict")}</th>
                  <th style={th}>{t("continuous.aiReview.colSeverity")}</th>
                  <th style={th}>{t("continuous.aiReview.colAction")}</th>
                  <th style={th}>{t("continuous.aiReview.colEvaluator")}</th>
                  <th style={th}>{t("continuous.aiReview.colSession")}</th>
                  <th style={th}>{t("continuous.aiReview.colScore")}</th>
                  <th style={th}>{t("continuous.aiReview.colRootCause")}</th>
                  <th style={th}>{t("continuous.aiReview.colSuggestion")}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => (
                  <tr key={`${r.session_id}-${r.trace_id || ""}-${r.evaluator}-${i}`}>
                    <td style={td}><Pill text={r.analysis.verdict || "–"} style={VERDICT_STYLE[r.analysis.verdict || ""]} /></td>
                    <td style={td}><Pill text={r.analysis.severity || "–"} style={SEVERITY_STYLE[r.analysis.severity || ""]} /></td>
                    <td style={td}><code>{r.analysis.action || "–"}</code></td>
                    <td style={td}>
                      {r.evaluator}
                      <AskEvalAgent question={t("askAgent.aiReviewItem", {
                        runId: requestId,
                        evaluator: r.evaluator,
                        sessionId: r.session_id || "—",
                        traceId: r.trace_id || "—",
                        verdict: r.analysis.verdict || "—",
                        severity: r.analysis.severity || "—",
                        action: r.analysis.action || "—",
                        score: askValue(r.score),
                        threshold: askValue(r.threshold),
                        rootCause: r.analysis.root_cause || "—",
                      })} />
                      {r.level ? <div style={{ color: "var(--eval-text-secondary)" }}>{r.level}</div> : null}
                    </td>
                    <td style={td} title={r.session_id}><code>{(r.session_id || "").slice(0, 18)}</code></td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{num(r.score)} / {num(r.threshold)}</td>
                    <td style={{ ...td, minWidth: 220 }}>{r.analysis.root_cause || "–"}</td>
                    <td style={{ ...td, minWidth: 220 }}>{r.analysis.suggestion || "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
